/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editWitnessProction2 } from "services/witnessProtectionService";
import { WitnessProtection } from "types";
import { BASE_URL, LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  protection: WitnessProtection;
  inline?: boolean;
  onClose: (refetch: boolean) => void;
};

const inputClass =
  "w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const ProtectionAssignProsecutor: FC<Props> = ({
  protection,
  inline,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [prosecutors, setProsecutors] = useState<
    {
      user: {
        id: string;
        first_name: string;
        last_name: string;
        employee_organizational_unit: string;
      };
    }[]
  >([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const fetchData = (adminId: string | number, orgId: string | number) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching prosecutors:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData]);

  const validateForm = () => {
    if (!prosecutors || prosecutors.length === 0)
      return t("no_prosecutors_available");
    if (!selectedId) return t("prosecutor_is_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    const api = editWitnessProction2;

    api({
      id: protection.id,
      assign_prosecutor_name: selectedName,
      assign_prosecutor_id: selectedId,
      status: "Assigned to Prosecutor",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const field = (
    <div>
      <label className={labelClass}>
        {t("witness_protection.modals.assign_prosecutor.prosecutor_label")}
      </label>
      <select
        className={`${inputClass} pr-8`}
        name="reception_method"
        value={selectedId || ""}
        onChange={(e) => {
          const selectedProsecutor = prosecutors.find(
            (p) => p?.user?.id?.toString() === e.target.value,
          );
          if (selectedProsecutor) {
            setSelectedId(
              selectedProsecutor.user?.id !== undefined &&
                selectedProsecutor.user?.id !== null
                ? String(selectedProsecutor.user?.id)
                : "",
            );
            setSelectedName(
              selectedProsecutor.user?.first_name +
                " " +
                selectedProsecutor.user?.last_name,
            );
          }
        }}
        required
      >
        <option value={""}>
          {loading
            ? t("witness_protection.modals.assign_prosecutor.loading")
            : t(
                "witness_protection.modals.assign_prosecutor.select_prosecutor",
              )}
        </option>
        {Array.isArray(prosecutors) &&
          prosecutors.map((p, index) => (
            <option key={index} value={p.user?.id || ""}>
              {p?.user?.first_name + " " + p?.user?.last_name}
            </option>
          ))}
      </select>
    </div>
  );

  const submitLabel = loading ? (
    <span className="flex items-center justify-center gap-2">
      {LoadingIcon}
      {t("loading")}
    </span>
  ) : (
    t("witness_protection.modals.assign_prosecutor.assign")
  );

  // Inline mode: render as a card inside the page (e.g. the right panel).
  if (inline) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center text-red-600 text-base">
              👨‍⚖️
            </div>
            <h3 className="text-base font-semibold text-gray-900">
              {t("witness_protection.modals.assign_prosecutor.title")}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            aria-label={t("cancel", { defaultValue: "Cancel" })}
          >
            ×
          </button>
        </div>
        <form className="p-5 space-y-4" onSubmit={handleSubmit}>
          {field}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className={`px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("witness_protection.modals.assign_prosecutor.title")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("witness_protection.modals.assign_prosecutor.form_description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {field}
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProtectionAssignProsecutor;
